import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { isValid as isValidDate, parseISO } from "date-fns";
import { Calendar, ClipboardCheck } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Button,
  Card,
  CardContent,
  MultiAutocomplete,
  Stack,
  Switch,
  TextField,
  Typography,
} from "../ds";
import type { ChipColorVariant } from "../ds";
import AttachmentLink from "./AttachmentLink";
import AttachmentUploader, { type UploadedFile } from "./AttachmentUploader";
import {
  RECORD_COLUMN,
  RecordAttachments,
  RecordBlock,
  RecordCaption,
  RecordChips,
  RecordDetails,
  RecordedBy,
  RecordThread,
  type RecordDetail,
} from "./RecordSummary";
import CriteriaSheet from "./evaluation/CriteriaSheet";
import MemberMatrix, { type MemberCandidate } from "./evaluation/MemberMatrix";
import ScoreStrip from "./evaluation/ScoreStrip";
import type { Criterion, MemberScore } from "./evaluation/criteria";
import { useIsAuthorized } from "../hooks/useIsAuthorized";
import { useDepartmentNames } from "../hooks/useDepartments";
import { displayDepartment } from "../api/departments";
import {
  buildDepartmentSelectOptions,
  type DepartmentSelectOption,
} from "../utils/departments";
import {
  invalidateSuggestionCaches,
  submitSscEvaluation,
  submitWcEvaluation,
} from "../api/ess";
import { formatDate, formatDateTime } from "../utils/format";
import { apiErrorMessage } from "../utils/apiError";
import {
  deriveOutcome,
  rewardCategoryFor,
  SSC_CRITERIA,
  SSC_PASS_MARK,
  WC_CRITERIA,
  WC_PASS_MARK,
  suggestionEvaluations,
  verifierDepartments,
  type EvaluationRecord,
  type Suggestion,
} from "../types/ess";

// --- Config -------------------------------------------------------------
// One implementation, two configs. Criteria/pass-mark/outcome vocabulary
// are procedure-mandated (Annexure-I/II for scoring, Annexure-III/IV for
// the evaluation forms themselves) — see types/ess.ts for the source
// comment on WC_CRITERIA/SSC_CRITERIA.

interface CommitteeConfig {
  committee: "wc" | "ssc";
  title: string;
  criteria: readonly Criterion[];
  passMark: number;
  passMarkCopy: string;
  // Display labels for the outcome `deriveOutcome` produces. `in_process`
  // is never produced any more but stays listed so evaluations recorded
  // before the outcome became score-derived still render with a label.
  outcomes: { value: string; label: string }[];
  positiveOutcome: string;
  stateForCommittee: string;
  action: string;
  submit: (id: string, params: Record<string, unknown>) => Promise<Suggestion>;
}

const CONFIGS: Record<"wc" | "ssc", CommitteeConfig> = {
  wc: {
    committee: "wc",
    title: "Annexure III · Working Committee",
    criteria: WC_CRITERIA,
    passMark: WC_PASS_MARK,
    passMarkCopy: `${WC_PASS_MARK} or more recommends the suggestion to the SC.`,
    outcomes: [
      { value: "in_process", label: "In Process" },
      { value: "recommend", label: "Recommend" },
      { value: "not_recommend", label: "Not Recommend" },
    ],
    positiveOutcome: "recommend",
    stateForCommittee: "accepted_by_hr",
    action: "submit_wc_evaluation",
    submit: submitWcEvaluation,
  },
  ssc: {
    committee: "ssc",
    title: "Annexure IV · Steering Committee",
    criteria: SSC_CRITERIA,
    passMark: SSC_PASS_MARK,
    passMarkCopy: `${SSC_PASS_MARK} or more approves the suggestion and earns it a reward category.`,
    outcomes: [
      { value: "in_process", label: "In Process" },
      { value: "approved", label: "Approved" },
      { value: "rejected", label: "Rejected" },
    ],
    positiveOutcome: "approved",
    stateForCommittee: "accepted_by_wc",
    action: "submit_ssc_evaluation",
    submit: submitSscEvaluation,
  },
};

// --- Helpers -------------------------------------------------------------

function buildScoreSeed(
  criteria: readonly Criterion[],
  rec?: EvaluationRecord,
): Record<string, number | ""> {
  const seed: Record<string, number | ""> = {};
  criteria.forEach((c) => {
    const v = rec?.scores?.[c.key];
    seed[c.key] = typeof v === "number" ? v : "";
  });
  return seed;
}

function seedMeetingDate(rec?: EvaluationRecord): Date | null {
  if (!rec?.meeting_date) return null;
  const d = parseISO(rec.meeting_date);
  return isValidDate(d) ? d : null;
}

function clampScore(raw: string, max: number): number | "" {
  if (raw.trim() === "") return "";
  const n = Math.trunc(Number(raw));
  if (Number.isNaN(n)) return "";
  return Math.max(0, Math.min(max, n));
}

function clampMemberScore(raw: string, max: number): number {
  if (raw.trim() === "") return 0;
  const n = Math.trunc(Number(raw));
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(max, n));
}

/**
 * Round-half-to-even (banker's rounding) — mirrors Python's built-in
 * `round()` semantics, which the backend uses when it recomputes the
 * committee scores from `member_scores` rows (the submitted top-level
 * scores are ignored when member rows are present). `Math.round` rounds
 * .5 halves UP (14.5 → 15) while Python rounds them to the nearest EVEN
 * integer (14.5 → 14, 15.5 → 16); using Math.round here would let the
 * FE preview total — and the outcome derived from it — disagree with what
 * the server actually stores and enforces.
 */
function roundHalfEven(x: number): number {
  const floor = Math.floor(x);
  if (x - floor === 0.5) {
    return floor % 2 === 0 ? floor : floor + 1;
  }
  return Math.round(x);
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function outcomeMeta(
  cfg: CommitteeConfig,
  outcome: string,
): { label: string; tone: ChipColorVariant } {
  const opt = cfg.outcomes.find((o) => o.value === outcome);
  const label = opt?.label ?? outcome;
  const tone: ChipColorVariant =
    outcome === cfg.positiveOutcome
      ? "success"
      : outcome === "in_process"
        ? "warning"
        : "error";
  return { label, tone };
}

// --- Small presentational bits -------------------------------------------

function SectionHeader({
  icon,
  title,
}: {
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2.5 }}>
      <Box
        sx={(theme) => ({
          width: 28,
          height: 28,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main",
        })}
      >
        {icon}
      </Box>
      <Typography
        sx={{ fontSize: 16, fontWeight: 600, color: "primary.main", flex: 1 }}
      >
        {title}
      </Typography>
    </Stack>
  );
}

// The two-column rhythm of the form's lower half: a fixed first column
// (meeting date, the Attachments label) and a flexible second one.
const FORM_ROW_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    sm: "220px minmax(0, 1fr)",
  },
  gap: 2,
  alignItems: "start",
} as const;

// Lucide calendar glyph for the "Meeting date" DatePicker's open button —
// x-date-pickers defaults to its own calendar glyph. Static; color
// inherits from the picker's IconButton via currentColor.
const CalendarPickerIcon = () => (
  <RavenIcon icon={Calendar} sx={{ fontSize: 18 }} />
);

interface EvaluationSummaryProps {
  committee: "wc" | "ssc";
  evalRecord: EvaluationRecord;
}

/**
 * A submitted evaluation, read-only — the permanent audit record once the
 * suggestion moves on. Renders the same sheet the editor does, with the
 * chosen bands still highlighted, so the print view and the detail page
 * show one layout rather than two that drift apart.
 */
export function EvaluationSummary({
  committee,
  evalRecord,
}: EvaluationSummaryProps) {
  const cfg = CONFIGS[committee];
  const outcome = outcomeMeta(cfg, evalRecord.outcome);
  const attachmentIds = evalRecord.attachment_ids ?? [];
  const verifierDepts = verifierDepartments(evalRecord);
  const values: Record<string, number | ""> = {};
  cfg.criteria.forEach((c) => {
    const v = evalRecord.scores?.[c.key];
    values[c.key] = typeof v === "number" ? v : "";
  });

  const memberNames = (evalRecord.member_scores ?? [])
    .map((m) => m.member_name)
    .filter(Boolean);
  const details: RecordDetail[] = [];
  if (evalRecord.meeting_date) {
    details.push({
      label: "Meeting date",
      column: RECORD_COLUMN.subject,
      value: formatDate(evalRecord.meeting_date),
    });
  }
  if (memberNames.length > 0) {
    details.push({
      label: "Scored by",
      column: RECORD_COLUMN.detail,
      value: <RecordChips labels={memberNames} />,
    });
  }
  if (evalRecord.evaluated_by_name || evalRecord.evaluated_at) {
    details.push({
      label: "Evaluated by",
      column: RECORD_COLUMN.recordedBy,
      value: (
        <RecordedBy
          name={evalRecord.evaluated_by_name ?? "Unknown"}
          at={
            evalRecord.evaluated_at
              ? formatDateTime(evalRecord.evaluated_at)
              : undefined
          }
        />
      ),
    });
  }
  if (verifierDepts.length > 0) {
    details.push({
      label: "Verifier departments",
      span: true,
      value: <RecordChips labels={verifierDepts.map(displayDepartment)} />,
    });
  }

  return (
    <RecordBlock>
      <ScoreStrip
        total={evalRecord.total}
        passMark={cfg.passMark}
        outcome={outcome}
        badge={
          cfg.committee === "ssc" && evalRecord.reward_category
            ? {
                label: `Reward Category: ${evalRecord.reward_category}`,
                tone: "primary",
              }
            : null
        }
      />
      <CriteriaSheet criteria={cfg.criteria} values={values} readOnly />
      {memberNames.length > 0 && (
        <RecordCaption>
          Committee score is the rounded per-criterion average of{" "}
          {memberNames.length} member{memberNames.length === 1 ? "" : "s"}.
        </RecordCaption>
      )}
      {/* The record parts every read-only card on the detail page shares
          (RecordSummary): labelled grid, comment panel, attachments. */}
      <RecordDetails items={details} />
      {/* The evaluator's comment reads like every other comment on the
          page (RecordThread): who wrote it, when, then the text. */}
      {evalRecord.comment && (
        <RecordThread
          label="Comment"
          showCount={false}
          entries={[
            {
              author: evalRecord.evaluated_by_name ?? "Unknown",
              at: evalRecord.evaluated_at
                ? formatDateTime(evalRecord.evaluated_at)
                : undefined,
              text: evalRecord.comment,
            },
          ]}
        />
      )}
      <RecordAttachments ids={attachmentIds} />
    </RecordBlock>
  );
}

// --- Main component -------------------------------------------------------

interface Props {
  committee: "wc" | "ssc";
  suggestion: Suggestion;
}

/**
 * Shared WC/SC evaluation form card (Annexure-III / Annexure-IV). One
 * implementation, two configs (`committee` picks criteria/pass mark/
 * outcome vocabulary/action). Self-gating:
 *  - the editor renders only when the suggestion is on the committee's
 *    review state AND the `submit_{wc,ssc}_evaluation` auth probe passes;
 *  - a read-only summary renders whenever an evaluation exists for that
 *    committee, for any viewer, on any state (it doubles as the audit
 *    record once the suggestion has moved on);
 *  - otherwise the component renders nothing.
 *
 * The outcome is not chosen: `deriveOutcome` reads it off the total, so
 * the score and the decision can never disagree.
 *
 * SC-only extras (`committee === "ssc"` conditionals): a
 * verifier-departments multi-select required only when the derived
 * outcome is Approved (client mirror of the server rule), and a live
 * reward-category preview (`rewardCategoryFor(total)`) once the total
 * clears `SSC_PASS_MARK`. The server remains canonical for
 * `reward_category` — it comes back on refetch and is not recomputed
 * client-side once stored.
 */
export default function EvaluationFormCard({ committee, suggestion }: Props) {
  const cfg = CONFIGS[committee];
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const existing = suggestionEvaluations(suggestion)[committee];
  const isCommitteeState = suggestion.status === cfg.stateForCommittee;

  const authQ = useIsAuthorized(
    {
      action: cfg.action,
      entity_type: "ess_suggestion",
      entity_id: suggestion.id,
      workflow: "ess_suggestion",
    },
    [suggestion.status],
  );
  const isAuthorizedForAction = authQ.data === true;
  const canEdit = isCommitteeState && isAuthorizedForAction;

  const [editingOpen, setEditingOpen] = useState(false);
  const [scores, setScores] = useState<Record<string, number | "">>(() =>
    buildScoreSeed(cfg.criteria, existing),
  );
  const [comment, setComment] = useState<string>(existing?.comment ?? "");
  const [meetingDate, setMeetingDate] = useState<Date | null>(() =>
    seedMeetingDate(existing),
  );
  const [newFiles, setNewFiles] = useState<UploadedFile[]>([]);
  const [scoreByMember, setScoreByMember] = useState<boolean>(
    !!existing?.member_scores?.length,
  );
  const [memberScores, setMemberScores] = useState<MemberScore[]>(
    existing?.member_scores ?? [],
  );
  // The people seated on this committee for this suggestion — offered first
  // in the member picker, and the rows "Score by member" starts from.
  const committeeRoster = useMemo<MemberCandidate[]>(
    () =>
      (suggestion.assigned_reviewers ?? [])
        .filter((r) => r.committee === (committee === "wc" ? "wc" : "sc"))
        .map((r) => ({ id: r.user_id, name: r.user_name ?? r.user_id })),
    [suggestion.assigned_reviewers, committee],
  );

  // SC-only extra. Seeded/reset alongside the fields above even for a WC
  // instance — harmless, since the JSX that reads it is gated by
  // `committee === "ssc"`.
  const [verifierDepts, setVerifierDepts] = useState<string[]>(() =>
    verifierDepartments(existing),
  );

  // useDepartmentNames() is called unconditionally (hooks can't be
  // conditional) even for the WC instance — same tradeoff already made
  // for the auth probe above; the ["departments"] query is shared/cached
  // across both card instances so this isn't an extra network request.
  const { departments: departmentList } = useDepartmentNames();
  const verifierDepartmentOptions = useMemo<DepartmentSelectOption[]>(
    () => buildDepartmentSelectOptions(departmentList),
    [departmentList],
  );
  const verifierDepartmentGroupBy = verifierDepartmentOptions.some(
    (o) => o.group,
  )
    ? (o: DepartmentSelectOption) => o.group ?? "Common"
    : undefined;

  const derivedScores = useMemo<Record<string, number> | null>(() => {
    if (!scoreByMember) return null;
    const out: Record<string, number> = {};
    cfg.criteria.forEach((c) => {
      if (memberScores.length === 0) {
        out[c.key] = 0;
        return;
      }
      const sum = memberScores.reduce(
        (acc, m) => acc + (Number(m.scores[c.key]) || 0),
        0,
      );
      // Half-even, not Math.round — must match the backend's Python
      // round() when it recomputes committee scores from member rows.
      out[c.key] = roundHalfEven(sum / memberScores.length);
    });
    return out;
  }, [scoreByMember, memberScores, cfg.criteria]);

  const effectiveScores: Record<string, number | ""> = derivedScores ?? scores;

  const total = useMemo(
    () =>
      Object.values(effectiveScores).reduce<number>(
        (sum, v) => sum + (typeof v === "number" ? v : 0),
        0,
      ),
    [effectiveScores],
  );

  // Every member row names a picked user, once each — the backend 400s
  // otherwise. Older records may hold typed-in names; re-editing one means
  // picking the user it meant.
  const memberIds = memberScores.map((m) => m.member_user_id).filter(Boolean);
  const membersUnpicked =
    scoreByMember && memberScores.some((m) => !m.member_user_id);
  const membersDuplicated =
    scoreByMember && new Set(memberIds).size !== memberIds.length;

  const allScored = scoreByMember
    ? memberScores.length > 0 &&
      cfg.criteria.every((c) => typeof derivedScores?.[c.key] === "number")
    : cfg.criteria.every((c) => typeof scores[c.key] === "number");

  const criteriaCount = cfg.criteria.length;
  const scoredCount = cfg.criteria.filter(
    (c) =>
      typeof (scoreByMember ? derivedScores?.[c.key] : scores[c.key]) ===
      "number",
  ).length;

  // The decision follows the score: a total at or past the pass mark is
  // always the positive outcome and anything below it the negative one,
  // so score and outcome cannot disagree and neither needs validating.
  const outcome = deriveOutcome(committee, total);
  const outcomeChip = outcomeMeta(cfg, outcome);

  // SC-only extra. `positiveOutcome` is "approved" for the ssc config, so
  // these are inert (always false) on the WC instance.
  const verifierRequired =
    committee === "ssc" && outcome === cfg.positiveOutcome;
  const blockedByVerifier = verifierRequired && verifierDepts.length === 0;
  // Second chip on the score strip. The reward category is a live,
  // score-driven preview of what the suggestion would earn — shown for SC
  // as soon as the total clears the pass mark, independent of the outcome
  // (it's a property of the score, not the decision). Below the pass mark,
  // once every criterion is scored, we flag the shortfall instead. The two
  // are mutually exclusive, and neither shows on a partially-filled form.
  const showRewardBadge = committee === "ssc" && total >= SSC_PASS_MARK;
  const belowPassMarkFlag = allScored && total < cfg.passMark;
  const stripBadge: { label: string; tone: ChipColorVariant } | null =
    belowPassMarkFlag
      ? { label: "Below pass mark", tone: "warning" }
      : showRewardBadge
        ? {
            label: `Reward Category: ${rewardCategoryFor(total)}`,
            tone: "success",
          }
        : null;

  const existingAttachmentIds = useMemo(
    () => existing?.attachment_ids ?? [],
    [existing],
  );

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      cfg.submit(suggestion.id, payload),
    onSuccess: () => {
      enqueueSnackbar(`${cfg.committee.toUpperCase()} evaluation submitted`, {
        variant: "success",
      });
      invalidateSuggestionCaches(qc, suggestion.id);
      setEditingOpen(false);
    },
    onError: (err: unknown) => {
      enqueueSnackbar(apiErrorMessage(err, "Could not submit evaluation"), {
        variant: "error",
      });
    },
  });

  const showEditor = canEdit && (!existing || editingOpen);

  const submitHint = !allScored
    ? `All ${criteriaCount} criteria must be scored to submit — ${scoredCount} of ${criteriaCount} done.`
    : membersUnpicked
      ? "Pick a user for every member row."
      : membersDuplicated
        ? "Each member can be scored only once."
        : null;

  const handleStartEdit = () => {
    setScores(buildScoreSeed(cfg.criteria, existing));
    setComment(existing?.comment ?? "");
    setMeetingDate(seedMeetingDate(existing));
    setNewFiles([]);
    setScoreByMember(!!existing?.member_scores?.length);
    setMemberScores(existing?.member_scores ?? []);
    setVerifierDepts(verifierDepartments(existing));
    setEditingOpen(true);
  };

  const handleScoreChange = (key: string, raw: string) => {
    const max = cfg.criteria.find((c) => c.key === key)?.max ?? 0;
    setScores((prev) => ({ ...prev, [key]: clampScore(raw, max) }));
  };

  const handleBandPick = (key: string, v: number) => {
    setScores((prev) => ({ ...prev, [key]: v }));
  };

  const addMember = () => {
    setMemberScores((prev) => [
      ...prev,
      { member_name: "", member_user_id: null, scores: {} },
    ]);
  };
  // Turning member scoring on for the first time starts from the seated
  // committee, one row each; rows stay removable.
  const toggleScoreByMember = (on: boolean) => {
    setScoreByMember(on);
    if (on && memberScores.length === 0 && committeeRoster.length > 0) {
      setMemberScores(
        committeeRoster.map((c) => ({
          member_name: c.name,
          member_user_id: c.id,
          scores: {},
        })),
      );
    }
  };
  const removeMember = (idx: number) => {
    setMemberScores((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateMember = (
    idx: number,
    who: Pick<MemberScore, "member_name" | "member_user_id">,
  ) => {
    setMemberScores((prev) =>
      prev.map((m, i) => (i === idx ? { ...m, ...who } : m)),
    );
  };
  const updateMemberScore = (idx: number, key: string, raw: string) => {
    const max = cfg.criteria.find((c) => c.key === key)?.max ?? 0;
    const n = clampMemberScore(raw, max);
    setMemberScores((prev) =>
      prev.map((m, i) =>
        i === idx ? { ...m, scores: { ...m.scores, [key]: n } } : m,
      ),
    );
  };

  const handleSubmit = () => {
    const finalScores: Record<string, number> = {};
    cfg.criteria.forEach((c) => {
      const v = effectiveScores[c.key];
      finalScores[c.key] = typeof v === "number" ? v : 0;
    });

    const payload: Record<string, unknown> = {
      scores: finalScores,
      outcome,
    };

    if (scoreByMember && memberScores.length > 0) {
      payload.member_scores = memberScores.map((m) => ({
        member_name: m.member_name.trim(),
        member_user_id: m.member_user_id,
        scores: Object.fromEntries(
          cfg.criteria.map((c) => [c.key, Number(m.scores[c.key]) || 0]),
        ),
      }));
    }
    if (comment.trim()) payload.comment = comment.trim();

    // Gated on verifierRequired (the derived outcome is Approved), not
    // just on the field holding values: a score edited back below the
    // pass mark flips the outcome to Rejected, and the departments picked
    // while it was passing must not ride into a non-approval payload
    // (they're an approval-only attribute). Same guard covers re-edits
    // seeded from a previously-Approved record.
    if (verifierRequired && verifierDepts.length > 0) {
      payload.verifier_departments = verifierDepts;
    }

    const allAttachmentIds = [
      ...existingAttachmentIds,
      ...newFiles.map((f) => f.asset_id),
    ];
    if (allAttachmentIds.length > 0) payload.attachment_ids = allAttachmentIds;
    if (meetingDate) payload.meeting_date = ymd(meetingDate);

    mutation.mutate(payload);
  };

  if (!showEditor && !existing) return null;

  return (
    <Card
      sx={{ mb: 2.5 }}
      data-tour={committee === "wc" ? "evaluation-wc" : "evaluation-ssc"}
    >
      <CardContent>
        <SectionHeader
          icon={<RavenIcon icon={ClipboardCheck} sx={{ fontSize: 16 }} />}
          title={cfg.title}
        />
        {showEditor ? (
          <Stack spacing={2.5}>
            <ScoreStrip
              total={total}
              passMark={cfg.passMark}
              outcome={outcomeChip}
              badge={stripBadge}
              note={cfg.passMarkCopy}
              sticky
            />

            <CriteriaSheet
              criteria={cfg.criteria}
              values={effectiveScores}
              locked={scoreByMember}
              onChange={handleScoreChange}
              onPick={handleBandPick}
            />

            <Box>
              <Switch
                label="Score by member"
                checked={scoreByMember}
                onChange={(e) => toggleScoreByMember(e.target.checked)}
              />
              {scoreByMember && (
                <Box sx={{ mt: 1 }}>
                  <MemberMatrix
                    criteria={cfg.criteria}
                    members={memberScores}
                    committee={committeeRoster}
                    averages={derivedScores ?? {}}
                    onAdd={addMember}
                    onRemove={removeMember}
                    onMemberChange={updateMember}
                    onScore={updateMemberScore}
                  />
                </Box>
              )}
            </Box>

            {/* Meeting date and comment share one row and one field recipe
                (the ds TextField), date fixed-width, comment flexible. They
                sit above the conditional verifier field: a score that
                crosses the pass mark reveals that field, and anything under
                it would shift out from under the cursor mid-sentence. */}
            <Box sx={FORM_ROW_SX}>
              <DatePicker
                label="Meeting date"
                value={meetingDate}
                onChange={(d) => setMeetingDate(d)}
                // The classic field DOM renders a plain MUI TextField, which
                // the theme's text-field recipe styles like the Comment
                // beside it; the accessible-field DOM is a different input
                // the recipe does not reach.
                enableAccessibleFieldDOMStructure={false}
                slots={{ openPickerIcon: CalendarPickerIcon }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    helperText: "Optional",
                  },
                }}
              />
              <TextField
                label="Comment"
                multiline
                minRows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                slotProps={{
                  htmlInput: { maxLength: 500 },
                  formHelperText: { sx: { textAlign: "right", mx: 0 } },
                }}
                helperText={`${comment.length} / 500`}
              />
            </Box>

            {verifierRequired && (
              <MultiAutocomplete<DepartmentSelectOption>
                label="Verifier Departments"
                options={verifierDepartmentOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                value={verifierDepartmentOptions.filter((o) =>
                  verifierDepts.includes(o.value),
                )}
                onChange={(picked) =>
                  setVerifierDepts(picked.map((o) => o.value))
                }
                groupBy={verifierDepartmentGroupBy}
                error={blockedByVerifier}
                helperText={
                  blockedByVerifier
                    ? "At least one is required to approve — this is why Submit is disabled."
                    : undefined
                }
              />
            )}

            {/* Attachments as a labelled row on the same two-column rhythm:
                label under the date column, files under the comment. */}
            <Box sx={FORM_ROW_SX}>
              <Box sx={{ pt: { sm: 0.75 } }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  Attachments
                </Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  Optional · minutes, photos
                </Typography>
              </Box>
              <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                {existingAttachmentIds.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={1.5}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="flex-end"
                  >
                    {existingAttachmentIds.map((aid) => (
                      <AttachmentLink key={aid} assetId={aid} />
                    ))}
                  </Stack>
                )}
                <AttachmentUploader files={newFiles} onChange={setNewFiles} />
              </Stack>
            </Box>

            {/* Footer action bar: why Submit is disabled on the left (the
                verifier requirement is reported on its own field above),
                the actions on the right. */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1.5}
              sx={{ pt: 2, borderTop: "1px solid", borderColor: "divider" }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {submitHint && (
                  <Typography color="error" sx={{ fontSize: 12 }}>
                    {submitHint}
                  </Typography>
                )}
              </Box>
              {existing && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setEditingOpen(false)}
                  disabled={mutation.isPending}
                  fullWidth
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={
                  !allScored ||
                  blockedByVerifier ||
                  membersUnpicked ||
                  membersDuplicated ||
                  mutation.isPending
                }
                fullWidth
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {mutation.isPending
                  ? "Submitting…"
                  : existing
                    ? "Update Evaluation"
                    : "Submit Evaluation"}
              </Button>
            </Stack>
          </Stack>
        ) : (
          existing && (
            <Stack spacing={1.75}>
              <EvaluationSummary committee={committee} evalRecord={existing} />
              {canEdit && (
                <Button
                  variant="text"
                  onClick={handleStartEdit}
                  sx={{
                    alignSelf: "flex-start",
                    color: "primary.dark",
                    minWidth: "auto",
                    px: 1,
                  }}
                >
                  Edit evaluation
                </Button>
              )}
            </Stack>
          )
        )}
      </CardContent>
    </Card>
  );
}
